import { api } from '../api';

/**
 * Значок ПО на экране телефона (PWA) и push-уведомления (решения Ромы 05.10.2026; опись erp-mobile/ПЕРЕДАЧА-В-БОЙ.md §4.8).
 * «Служебный помощник» — public/sw.js: быстрый запуск каркаса, показ уведомлений, открытие нужного экрана по нажатию.
 * Push включается на устройстве сотрудником (спрашиваем при первом запуске на телефоне/планшете, потом — в «Ещё»);
 * события те же, что в боте «Продажи». iPhone: только у значка на экране «Домой» (iOS 16.4+) — в Safari поддержки нет.
 */

const ASKED_KEY = 'wg.push.asked';
export const OPEN_EVENT = 'wg-open';

/** Регистрация помощника: только в собранном ПО и по https (в dev помощник мешал бы обновлению модулей). */
export function registerPwa(): void {
  if (!import.meta.env.PROD || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => { void navigator.serviceWorker.register('/sw.js').catch(() => undefined); window.setTimeout(() => void resyncPush(), 5000); });
  // Нажатие на уведомление при открытом ПО: помощник просит открыть экран — передаём каркасу (AppShell слушает OPEN_EVENT).
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent) => {
    const d = e.data as { type?: string; url?: string } | null;
    if (d?.type === 'wg-open' && typeof d.url === 'string') window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: d.url }));
  });
}

export const pushSupported = (): boolean => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export type PushState = 'unsupported' | 'off' | 'on' | 'denied';

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try { return (await navigator.serviceWorker.getRegistration()) ?? null; } catch { return null; }
}

/** Состояние уведомлений на этом устройстве. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await registration();
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.getSubscription().catch(() => null);
  return sub && Notification.permission === 'granted' ? 'on' : 'off';
}

function keyBytes(base64url: string): Uint8Array {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
const b64 = (buf: ArrayBuffer | null): string => { if (!buf) return ''; let s = ''; for (const b of new Uint8Array(buf)) s += String.fromCharCode(b); return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); };

interface PushConfig { ready: boolean; public_key: string | null; enabled: boolean; devices_mine: number; devices_total: number }
export async function pushConfig(): Promise<PushConfig | null> {
  try { return (await api.query<PushConfig>('push.config', {})).data; } catch { return null; }
}

/** Включить уведомления на этом устройстве: разрешение телефона → подписка → запись в ПО. Возвращает итоговое состояние. */
export async function enablePush(): Promise<PushState> {
  markPushAsked();
  if (!pushSupported()) return 'unsupported';
  const cfg = await pushConfig();
  const reg = await registration();
  if (!cfg?.ready || !cfg.public_key || !reg) return 'unsupported';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'off';
  try {
    const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(cfg.public_key) as BufferSource }));
    await api.command('push.subscribe', { endpoint: sub.endpoint, p256dh: b64(sub.getKey('p256dh')), auth: b64(sub.getKey('auth')), user_agent: navigator.userAgent.slice(0, 300) });
    return 'on';
  } catch { return 'off'; }
}

/** Подписка устройства могла смениться или пропасть в ПО (обновление браузера, переустановка значка, ответ «подписки нет» от производителя) —
 * при каждом запуске, если разрешение дано и подписка на устройстве есть, записываем её в ПО заново (Рома 26.10.2026: push «особо не приходит»; на бою было 0 устройств). */
export async function resyncPush(): Promise<void> {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await api.command('push.subscribe', { endpoint: sub.endpoint, p256dh: b64(sub.getKey('p256dh')), auth: b64(sub.getKey('auth')), user_agent: navigator.userAgent.slice(0, 300) });
  } catch { /* нет сессии или сети — повторим при следующем запуске */ }
}

export async function disablePush(): Promise<PushState> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription().catch(() => null);
  if (sub) {
    try { await api.command('push.unsubscribe', { endpoint: sub.endpoint }); } catch { /* подписка на устройстве всё равно снимается */ }
    await sub.unsubscribe().catch(() => undefined);
  }
  return pushSupported() ? 'off' : 'unsupported';
}

/** Спрашивали ли уже про уведомления на этом устройстве (вопрос — один раз, дальше только из «Ещё»). */
export const pushAsked = (): boolean => { try { return localStorage.getItem(ASKED_KEY) === '1'; } catch { return true; } };
export const markPushAsked = (): void => { try { localStorage.setItem(ASKED_KEY, '1'); } catch { /* приватный режим */ } };
/** Пора ли спросить: устройство умеет, разрешение ещё не давали и не запрещали, ПО настроено, раньше не спрашивали. */
export async function shouldAskPush(): Promise<boolean> {
  if (!pushSupported() || pushAsked() || Notification.permission !== 'default') return false;
  if (!(await registration())) return false;
  const cfg = await pushConfig();
  return !!cfg?.ready && cfg.enabled;
}

/** Число дел на значке ПО (там, где телефон это умеет). */
export function setAppBadge(n: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try { if (n > 0) void nav.setAppBadge?.(n).catch(() => undefined); else void nav.clearAppBadge?.().catch(() => undefined); } catch { /* не поддерживается */ }
}
